﻿

/**
    The state object hold a map of properties that describe the state of the 
    application. You can attach watchers to any property name to know when
    anything has changed. You can change the state by calling change.
    The state property tree is serialized, meaning there is no nested object
    structure keeping things simpler. State values are of type string, int,
    float, boolean or array. No objects, maps, sets and functions.

    for instance you can change a date property of the state consisting of 
    year, month and day like this:
    
    change('date', {year: 2016, month: 8, day: 26});
    
    This will result in the following state object:

    ['date.year', 2016],
    ['date.month', 8],
    ['date.day', 26]

    And will call the following 4 watchers if available:
    'date.year', 'date.month', 'date.day' AND 'date'

    Note when you would set change('date', '2016-08-27')
    Your state changes to:
    ['date.year', null],
    ['date.month', null],
    ['date.day', null],
    ['date', '2016-08-27']

    All watcher are called

    If you call change('date', {year: 2016, month: 9});
    you will unset date.day (watcher will be called)

    If you need to work with objects, you'll have to create the logic for
    this in your components.


**/
import {proxy, cloneObject, stringify} from './functions';
import Responder from './responder';

class State extends Responder{

    constructor() {
        // bootstrap stuff
        super();

        this.name = 'state';

        this.stateHistory = new Set();
        this.state = new Map();
        this.stateTree = new Map();
        this.stateWatchers = new Map();
        this.previousState = new Map();

        this.historyItemLimit = 100;

        this.forceGlobalUpdate = false;
        this.someUpdate = true;
        this.keepHistory = false;
        this.timerId = null;

    }

    getProperties(name, value, map = new Map()){

        if(value != null && value instanceof Object && 
                ! (value instanceof Array) && ! (value instanceof Map) && 
                !  (value instanceof Set) ){
            for(let key of Object.keys(value)){
                map = this.getProperties(name + '.' + key, value[key], map);
            }
        }else{
            if(value instanceof Array){
                map.set(name, value.slice());
            }else{
                map.set(name, value);
            }
        }

        return map;
    }


    set(name, value) {

        if(stringify(this.get(name)) == stringify(value)){
            return;
        }

        let state = new Map(this.state);
        let watchList = [];

        // remove the node if set
        this.state.delete(name);

        // remove any set children for this node
        for(let [k, v] of state){
            if(k.length > name.length && k.substring(0, name.length+1) == name+'.'){
                this.state.delete(k);
                if(v != '__NOT_SET__'){
                    watchList.push(k);
                }
            }
        }
        // remove any set parents for this node (will recompile when calling get)
        let parts = name.split('.');

        let fn = [];
        for(let i = 0; i < parts.length-1; i++){
            fn.push(parts[i]);
            this.state.delete(fn.join('.'));
        }

        let map = this.getProperties(name, value);


        for( let [key, value] of map) {
            let oldValue = (state.has(key))? state.get(key): null;
            
            let changed = true;
            if(value instanceof Array && oldValue instanceof Array){
                
                if(value.length == oldValue.length){
                    changed = false;
                    for(let i = 0; i < value.length; i++){
                        if(value[i] !== oldValue[i]){
                            changed = true;
                            break;
                        }
                    }
                }
            }else if(oldValue === value){
                changed = false;
            }

            if(value == null){
                this.state.delete(key);
                if(oldValue == '__NOT_SET__'){
                    changed = false;
                }
            }else{
                this.state.set(key, value);
            }
            if(changed){
                watchList.push(key);
            }
        };

        // this.addParentsToWatchlist(watchList, state);

        this.setStateHistory(state);
        if(watchList.length > 0){
            let done = new Set();
            for(let key of watchList){
                if(! done.has(key)){
                    this.trigger(key);
                    // console.log('STATE: ' + key + ' = ' + this.state.get(key));
                    done.add(key);
                }
            }
        }

        
    }

    setDefault(name, value){
        if(! this.get(name)){
            this.set(name, value);
        }
    }
    
    setStateHistory(state){
        this.previousState = state;
        if(this.keepHistory){
            clearTimeout(this.timerId);
            this.timerId = setTimeout(proxy([this, function(){
                this.stateHistory.add(state);
                this.flushHistory(this.historyItemLimit);
            }]), 500);
        }
    }

    get(name){
        return this.getValue(name, this.state);
    }

    getPrevious(name){
        return this.getValue(name, this.previousState);
    }

    getValue(name, map){
        if(map.has(name)){
            let v = map.get(name);
            if(v == '__NOT_SET__'){
                return false;
            }
            return cloneObject(v);
        }else{
            let result = {};
            let found = false;
            for(let [k, v] of map){
                if(v == '__NOT_SET__'){
                    continue;
                }

                if(name == '*' || (k.length > name.length && 
                        k.substring(0, name.length+1) == name+'.')){
                    let value = cloneObject(v);
                    found = true;
                    let path = (name=='*')? k.split('.'): 
                            k.substring(name.length+1).split('.');
                    let r = result;
                    for(let i = 0; i < path.length; i++){
                        if(typeof r[path[i]] == 'undefined'){
                            r[path[i]] = (i < path.length-1)? {}: value;
                        }
                        r = r[path[i]];
                    }
                }
            }
            if(found){ // save the result for later re-use
                map.set(name, result);
                return result;
            }
            map.set(name, '__NOT_SET__');
            return false;
        }
    }

    setState(state) {

        this.stateHistory.add(new Map(this.state));
        this.state = state;
        this.trigger('*');

        this.flushHistory(this.historyItemLimit);

    }

    flushHistory(limit){
        if(typeof limit === 'undefined'){
            this.stateHistory.clear();
            return;
        }
        if(this.stateHistory.size > limit){
            let remove = this.stateHistory.size - limit;
            for(let v of this.stateHistory){
                this.stateHistory.delete(v);
                remove--;
                if(remove <= 0){
                    return;
                }
            }
        }
    }


}

let stateInstance = new State();
export default stateInstance;